import { useCallback, useMemo, useState } from 'react';

import type { ReportWidgetItem, ReportWidgetPagination } from '../types';

interface UseComposableWidgetStateParams {
    items: ReportWidgetItem[];
    pagination?: ReportWidgetPagination;
    loading: boolean;
}

interface UseComposableWidgetStateResult {
    visibleItems: ReportWidgetItem[];
    isInitialLoading: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
    loadMoreCount: number;
    handleLoadMore: () => void;
    canCollapse: boolean;
    handleCollapse: () => void;
}

export const useComposableWidgetState = ({
    items,
    pagination,
    loading,
}: UseComposableWidgetStateParams): UseComposableWidgetStateResult => {
    const usesClientPagination = pagination?.mode === 'client';
    const usesServerPagination = pagination?.mode === 'server';

    const initialLimit =
        usesClientPagination && pagination ? pagination.pageSize : items.length;

    const [limit, setLimit] = useState(initialLimit);

    const visibleItems = useMemo(
        () => (usesClientPagination ? items.slice(0, limit) : items),
        [items, limit, usesClientPagination],
    );

    const internalHasMore =
        usesClientPagination && items.length > 0 && limit < items.length;
    const externalHasMore =
        usesServerPagination && pagination ? pagination.canLoadMore : false;

    const hasMore = internalHasMore || externalHasMore;
    const loadMoreCount = internalHasMore ? items.length - limit : 0;

    const handleLoadMore = useCallback(() => {
        if (internalHasMore && usesClientPagination && pagination) {
            const revealAll =
                pagination.mode === 'client' &&
                pagination.revealAllOnLoadMore === true;
            const step = revealAll ? items.length : pagination.pageSize;
            setLimit((prev: number) => Math.min(prev + step, items.length));
            return;
        }

        if (externalHasMore && usesServerPagination && pagination) {
            pagination.onLoadMore();
        }
    }, [
        externalHasMore,
        internalHasMore,
        items.length,
        pagination,
        usesClientPagination,
        usesServerPagination,
    ]);

    const canCollapse =
        usesClientPagination &&
        items.length > initialLimit &&
        !internalHasMore &&
        items.length > 0;

    const handleCollapse = useCallback(() => {
        if (!usesClientPagination) {
            return;
        }
        setLimit(initialLimit);
    }, [initialLimit, usesClientPagination]);

    return {
        visibleItems,
        isInitialLoading: loading && items.length === 0,
        isLoadingMore: loading && items.length > 0,
        hasMore,
        loadMoreCount,
        handleLoadMore,
        canCollapse,
        handleCollapse,
    };
};
